/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Dialog } from '@headlessui/react'
import { createContext, useContext, useState } from 'react'
import type { PropsWithChildren } from 'react'

import AlertIcon from '@Icons/alert-icon.svg?react'

interface SPRuleFrequencyWarningState {
  isOpen: boolean
}

interface SPRuleFrequencyWarningDispatch {
  open: () => void
  close: () => void
  onApply: (callback?: () => void) => void
}

const SPRuleFrequencyWarningStateContext =
  createContext<SPRuleFrequencyWarningState>({} as SPRuleFrequencyWarningState)
SPRuleFrequencyWarningStateContext.displayName = 'SPRuleFrequencyWarningState'

const SPRuleFrequencyWarningDispatchContext =
  createContext<SPRuleFrequencyWarningDispatch>(
    {} as SPRuleFrequencyWarningDispatch,
  )
SPRuleFrequencyWarningDispatchContext.displayName =
  'SPRuleFrequencyWarningDispatch'

function SPRuleFrequencyWarningProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [applyCallback, setApplyCallback] = useState<() => void>(() => {
    setIsOpen(false)
  })

  const open = (): void => {
    setIsOpen(true)
  }

  const close = (): void => {
    setIsOpen(false)
  }

  const onApply = (callback?: () => void): void => {
    setApplyCallback(() => () => {
      setIsOpen(false)
      !!callback && callback()
    })
  }

  return (
    <SPRuleFrequencyWarningStateContext.Provider value={{ isOpen }}>
      <SPRuleFrequencyWarningDispatchContext.Provider
        value={{ open, close, onApply }}
      >
        {children}
        <SPRuleFrequencyWarningModal onApplyClick={applyCallback} />
      </SPRuleFrequencyWarningDispatchContext.Provider>
    </SPRuleFrequencyWarningStateContext.Provider>
  )
}

function useSPRuleFrequencyWarningState(): SPRuleFrequencyWarningState {
  try {
    const context = useContext(SPRuleFrequencyWarningStateContext)
    return context
  } catch (error) {
    throw new Error(
      'useSPRuleFrequencyWarningState must be used within a SPRuleFrequencyWarningProvider',
      { cause: error },
    )
  }
}

function useSPRuleFrequencyWarningDispatch(): SPRuleFrequencyWarningDispatch {
  try {
    const context = useContext(SPRuleFrequencyWarningDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useSPRuleFrequencyWarningDispatch must be used within a SPRuleFrequencyWarningProvider',
      { cause: error },
    )
  }
}

function SPRuleFrequencyWarningModal({
  onApplyClick,
}: {
  onApplyClick: () => void
}): JSX.Element {
  const { isOpen } = useSPRuleFrequencyWarningState()
  const { close } = useSPRuleFrequencyWarningDispatch()

  const handleClose = (): void => {
    close()
  }

  const handleApply = (): void => {
    close()
    onApplyClick()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      id="SPRuleFrequencyWarningModal"
      initialFocus={undefined}
    >
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          background: 'rgba(41, 41, 41, 0.7)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Dialog.Panel className="relative border rounded-xl shadow-xl bg-white-100 md:w-2/3 lg:w-[55%] xxl:w-[40%] !max-w-screen-sm w-full">
          <div className="lg:inline-flex lg:flex-row lg:space-x-6 lg:items-start lg:w-full flex flex-col justify-center items-center relative p-6">
            <div className="w-full">
              <div className="flex w-full justify-center items-center mt-2">
                <AlertIcon className="w-12 h-12 mr-3 [&>path]:stroke-orange-500" />
                <p className="mt-2 text-gray-700">
                  This rule will be immediately reflected to the product group
                  you have selected. Do you want to continue?
                </p>
              </div>
              <div className="lg:inline-flex lg:flex-row lg:justify-end flex flex-col justify-between lg:space-x-6 w-full mt-6">
                <button
                  type="button"
                  className="w-full mb-4 lg:px-10 lg:w-auto border border-solid border-gray-300 rounded-lg py-2.5 text-gray-700"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="w-full mb-4 lg:px-10 lg:w-auto py-2.5 bg-primary-600 border-solid border-primary-600 text-white rounded-lg"
                  onClick={handleApply}
                >
                  Yes, apply the rule
                </button>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export {
  SPRuleFrequencyWarningModal,
  SPRuleFrequencyWarningProvider,
  useSPRuleFrequencyWarningState,
  useSPRuleFrequencyWarningDispatch,
}
